import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { ModalBase } from "@/components/ModalBase/ModalBase";

interface TemplateItem {
  id: number;
  title: string;
  order: number;
}

interface Template {
  id: number;
  title: string;
  items: TemplateItem[];
}

interface ModalGerenciarChecklistProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyTemplate: (templateId: number, templateTitle: string) => void;
}

export function ModalGerenciarChecklist({
  open,
  onOpenChange,
  onApplyTemplate,
}: ModalGerenciarChecklistProps) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(
    null,
  );
  const [editingTemplateTitle, setEditingTemplateTitle] = useState("");
  const [newItemInputs, setNewItemInputs] = useState<Record<number, string>>(
    {},
  );
  const [showNewItemInput, setShowNewItemInput] = useState<
    Record<number, boolean>
  >({});
  const [editingItem, setEditingItem] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const newTemplateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) fetchTemplates();
  }, [open]);

  useEffect(() => {
    if (creatingTemplate && newTemplateInputRef.current) {
      newTemplateInputRef.current.focus();
    }
  }, [creatingTemplate]);

  async function fetchTemplates() {
    try {
      setLoading(true);
      const res = await ServiceResumoDesk.listChecklistTemplates();
      if (res?.status === 200) {
        setTemplates(res.data.data || []);
      }
    } catch {
      toast.error(t("checklist.errors.fetch"));
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateTemplate() {
    const title = newTemplateName.trim();
    if (!title) {
      setCreatingTemplate(false);
      return;
    }
    const res = await ServiceResumoDesk.createChecklistTemplate({ title });
    if (res?.status === 200) {
      setTemplates((prev) => [...prev, { ...res.data.data, items: [] }]);
      setNewTemplateName("");
      setCreatingTemplate(false);
    } else {
      toast.error(t("checklist.errors.create"));
    }
  }

  async function saveTemplateTitle(template: Template) {
    const title = editingTemplateTitle.trim();
    setEditingTemplateId(null);
    if (!title || title === template.title) return;

    setTemplates((prev) =>
      prev.map((t) => (t.id === template.id ? { ...t, title } : t)),
    );
    const res = await ServiceResumoDesk.updateChecklistTemplate(template.id, {
      title,
    });
    if (res?.status !== 200) {
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === template.id ? { ...t, title: template.title } : t,
        ),
      );
      toast.error(t("checklist.errors.update"));
    }
  }

  async function handleDeleteTemplate(id: number) {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    if (selectedTemplateId === id) setSelectedTemplateId(null);
    const res = await ServiceResumoDesk.deleteChecklistTemplate(id);
    if (res?.status !== 200) {
      toast.error(t("checklist.errors.delete"));
      fetchTemplates();
    }
  }

  async function handleAddItem(templateId: number) {
    const title = (newItemInputs[templateId] || "").trim();
    if (!title) {
      setShowNewItemInput((prev) => ({ ...prev, [templateId]: false }));
      return;
    }

    const template = templates.find((t) => t.id === templateId);
    const nextOrder =
      template && template.items.length > 0
        ? Math.max(...template.items.map((i) => i.order)) + 1
        : 0;

    const res = await ServiceResumoDesk.addChecklistTemplateItem(templateId, {
      title,
      order: nextOrder,
    });
    if (res?.status === 200) {
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === templateId
            ? { ...t, items: [...t.items, res.data.data] }
            : t,
        ),
      );
      setNewItemInputs((prev) => ({ ...prev, [templateId]: "" }));
      setShowNewItemInput((prev) => ({ ...prev, [templateId]: false }));
    } else {
      toast.error(t("checklist.errors.create"));
    }
  }

  async function saveItemEdit(templateId: number, item: TemplateItem) {
    const title = (editingItem?.title ?? "").trim();
    setEditingItem(null);
    if (!title || title === item.title) return;

    setTemplates((prev) =>
      prev.map((t) =>
        t.id === templateId
          ? {
              ...t,
              items: t.items.map((i) =>
                i.id === item.id ? { ...i, title } : i,
              ),
            }
          : t,
      ),
    );
    const res = await ServiceResumoDesk.updateChecklistTemplateItem(item.id, {
      title,
    });
    if (res?.status !== 200) {
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === templateId
            ? {
                ...t,
                items: t.items.map((i) =>
                  i.id === item.id ? { ...i, title: item.title } : i,
                ),
              }
            : t,
        ),
      );
      toast.error(t("checklist.errors.update"));
    }
  }

  async function handleDeleteItem(templateId: number, itemId: number) {
    setTemplates((prev) =>
      prev.map((t) =>
        t.id === templateId
          ? { ...t, items: t.items.filter((i) => i.id !== itemId) }
          : t,
      ),
    );
    const res = await ServiceResumoDesk.deleteChecklistTemplateItem(itemId);
    if (res?.status !== 200) {
      toast.error(t("checklist.errors.delete"));
      fetchTemplates();
    }
  }

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  return (
    <ModalBase open={open} onOpenChange={onOpenChange} size="md">
      <div className="bg-white rounded-3xl p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-[#1E1E1E]">
            {t("checklist.templates_title")}
          </h2>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-[#1E1E1E]/40 hover:text-[#1E1E1E] transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex gap-4 min-h-[320px]">
          {/* Lista de templates */}
          <div className="w-2/5 flex flex-col gap-2">
            <span className="text-xs font-medium text-[#1E1E1E]/50 uppercase tracking-wide">
              {t("checklist.templates_list")}
            </span>

            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((n) => (
                  <div
                    key={n}
                    className="h-9 bg-gray-100 rounded-xl animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-1 flex-1 overflow-y-auto">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={`group flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-colors ${
                      selectedTemplateId === template.id
                        ? "bg-azul-400/10 text-azul-400"
                        : "hover:bg-[#F1F1F1] text-[#1E1E1E]"
                    }`}
                    onClick={() => setSelectedTemplateId(template.id)}
                  >
                    {editingTemplateId === template.id ? (
                      <input
                        autoFocus
                        value={editingTemplateTitle}
                        onChange={(e) =>
                          setEditingTemplateTitle(e.target.value)
                        }
                        onBlur={() => saveTemplateTitle(template)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveTemplateTitle(template);
                          if (e.key === "Escape") setEditingTemplateId(null);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 text-sm border-b border-azul-400 outline-none bg-transparent"
                      />
                    ) : (
                      <span
                        className="flex-1 text-sm truncate"
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setEditingTemplateId(template.id);
                          setEditingTemplateTitle(template.title);
                        }}
                      >
                        {template.title}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(template.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[#1E1E1E]/30 hover:text-red-500 flex-shrink-0"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0zM4.544 3.5l.85 10.58a1 1 0 0 0 .997.92h6.23a1 1 0 0 0 .997-.92l.85-10.58z" />
                      </svg>
                    </button>
                  </div>
                ))}

                {/* Criar novo template */}
                {creatingTemplate ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F8F8F8]">
                    <input
                      ref={newTemplateInputRef}
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      onBlur={handleCreateTemplate}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCreateTemplate();
                        if (e.key === "Escape") {
                          setCreatingTemplate(false);
                          setNewTemplateName("");
                        }
                      }}
                      placeholder={t("checklist.template_name_placeholder")}
                      className="flex-1 text-sm outline-none bg-transparent placeholder:text-[#1E1E1E]/30"
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCreatingTemplate(true)}
                    className="flex items-center gap-1 text-xs text-[#1E1E1E]/40 hover:text-azul-400 transition-colors py-1 px-3"
                  >
                    <span className="text-base leading-none">+</span>
                    {t("checklist.new_template")}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Divisor */}
          <div className="w-px bg-black/5" />

          {/* Detalhes do template selecionado */}
          <div className="flex-1 flex flex-col gap-2">
            {selectedTemplate ? (
              <>
                <span className="text-xs font-medium text-[#1E1E1E]/50 uppercase tracking-wide">
                  {t("checklist.template_items")}
                </span>

                <ul className="space-y-0.5 flex-1 overflow-y-auto">
                  {selectedTemplate.items.map((item, index) => (
                    <li
                      key={item.id}
                      className="group flex items-center gap-2 py-1.5 px-2 rounded-xl hover:bg-[#F8F8F8] transition-colors"
                    >
                      <span className="text-xs text-[#1E1E1E]/30 w-4 flex-shrink-0">
                        {index + 1}
                      </span>
                      {editingItem?.id === item.id ? (
                        <input
                          autoFocus
                          value={editingItem.title}
                          onChange={(e) =>
                            setEditingItem({
                              id: item.id,
                              title: e.target.value,
                            })
                          }
                          onBlur={() => saveItemEdit(selectedTemplate.id, item)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              saveItemEdit(selectedTemplate.id, item);
                            if (e.key === "Escape") setEditingItem(null);
                          }}
                          className="flex-1 text-sm border-b border-azul-400 outline-none bg-transparent"
                        />
                      ) : (
                        <span
                          onClick={() =>
                            setEditingItem({ id: item.id, title: item.title })
                          }
                          className="flex-1 text-sm text-[#1E1E1E] cursor-text"
                        >
                          {item.title}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          handleDeleteItem(selectedTemplate.id, item.id)
                        }
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[#1E1E1E]/30 hover:text-red-500 flex-shrink-0"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                        >
                          <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0zM4.544 3.5l.85 10.58a1 1 0 0 0 .997.92h6.23a1 1 0 0 0 .997-.92l.85-10.58z" />
                        </svg>
                      </button>
                    </li>
                  ))}

                  {showNewItemInput[selectedTemplate.id] ? (
                    <li className="flex items-center gap-2 py-1.5 px-2 rounded-xl bg-[#F8F8F8]">
                      <span className="text-xs text-[#1E1E1E]/30 w-4 flex-shrink-0">
                        {selectedTemplate.items.length + 1}
                      </span>
                      <input
                        autoFocus
                        value={newItemInputs[selectedTemplate.id] || ""}
                        onChange={(e) =>
                          setNewItemInputs((prev) => ({
                            ...prev,
                            [selectedTemplate.id]: e.target.value,
                          }))
                        }
                        onBlur={() => handleAddItem(selectedTemplate.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            handleAddItem(selectedTemplate.id);
                          if (e.key === "Escape") {
                            setShowNewItemInput((prev) => ({
                              ...prev,
                              [selectedTemplate.id]: false,
                            }));
                            setNewItemInputs((prev) => ({
                              ...prev,
                              [selectedTemplate.id]: "",
                            }));
                          }
                        }}
                        placeholder={t("checklist.item_placeholder")}
                        className="flex-1 text-sm outline-none bg-transparent placeholder:text-[#1E1E1E]/30"
                      />
                    </li>
                  ) : (
                    <li>
                      <button
                        type="button"
                        onClick={() =>
                          setShowNewItemInput((prev) => ({
                            ...prev,
                            [selectedTemplate.id]: true,
                          }))
                        }
                        className="flex items-center gap-1 text-xs text-[#1E1E1E]/40 hover:text-azul-400 transition-colors py-1 px-2"
                      >
                        <span className="text-base leading-none">+</span>
                        {t("checklist.add_item")}
                      </button>
                    </li>
                  )}
                </ul>

                {/* Ação: Usar template */}
                <div className="pt-2 border-t border-black/5">
                  <button
                    type="button"
                    onClick={() =>
                      onApplyTemplate(
                        selectedTemplate.id,
                        selectedTemplate.title,
                      )
                    }
                    className="w-full py-2 text-sm bg-azul-400 text-white rounded-2xl hover:opacity-80 transition-opacity"
                  >
                    {t("checklist.apply_template")}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-sm text-[#1E1E1E]/30 text-center">
                  {templates.length === 0
                    ? t("checklist.no_templates")
                    : t("checklist.select_template")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalBase>
  );
}
